import * as Sentry from '@sentry/react';
import { extractErrorInfo, isPlainObject } from './errorInfo';
import { normalizeMetricKey, trackCount, trackDuration } from './sentryMetrics';

export interface ErrorContext {
  operation?: string;
  component?: string;
  userId?: string;
  [key: string]: unknown;
}

const SENSITIVE_KEY_PATTERNS = [
  'password',
  'token',
  'secret',
  'key',
  'apikey',
  'api_key',
  'api-key',
  'privatekey',
  'accesstoken',
  'refreshtoken',
  'authorization',
  'cookie',
  'rpcurl',
  'rpc_url',
  'dsn',
  'connectionstring',
  'credential',
];

const MAX_SERIALIZATION_DEPTH = 3;
const MAX_ARRAY_LENGTH = 20;
const MAX_STRING_LENGTH = 8192;
const SEVERITY_LEVELS = new Set<Sentry.SeverityLevel>([
  'fatal',
  'error',
  'warning',
  'log',
  'info',
  'debug',
]);

const EXPECTED_ERROR_NAMES = new Set(['InvalidFormData']);
const EXPECTED_ERROR_MESSAGES = new Set([
  'Email must be verified to perform this action',
  'Incorrect email and/or password',
  'Sign in failed',
]);
const EXPECTED_ERROR_MESSAGES_BY_NAME = new Map<string, Set<string>>([
  [
    'UserSignUpError',
    new Set([
      'User already exists',
      'Passwords do not match',
      'This email belongs to a previously removed account. Contact support to restore access.',
    ]),
  ],
]);
const EXPECTED_ERROR_MESSAGE_PATTERNS = [
  /^A release with version code \d+ already exists for this app$/,
  /^Insufficient SOL balance(?:\.| for\b)/,
];
const ERROR_EVENT_COUNT_METRIC = 'portal.web.error';
const TRPC_FAILURE_COUNT_METRIC = 'portal.web.trpc.failure';
const TRPC_FAILURE_DURATION_METRIC = 'portal.web.trpc.failure.duration';

function isExpectedError(name: string | undefined, message: string): boolean {
  if (name && EXPECTED_ERROR_NAMES.has(name)) {
    return true;
  }

  if (EXPECTED_ERROR_MESSAGES.has(message)) {
    return true;
  }

  if (name) {
    const expectedMessages = EXPECTED_ERROR_MESSAGES_BY_NAME.get(name);
    if (expectedMessages?.has(message)) {
      return true;
    }
  }

  if (
    EXPECTED_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(message))
  ) {
    return true;
  }

  return false;
}

function shouldRedactKey(key: string): boolean {
  const normalized = key.toLowerCase();
  return SENSITIVE_KEY_PATTERNS.some((pattern) => normalized.includes(pattern));
}

const SENSITIVE_URL_PARAMS = [
  'api-key',
  'apikey',
  'api_key',
  'key',
  'token',
  'secret',
  'password',
  'auth',
];

function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url);
    let hasRedacted = false;
    for (const param of SENSITIVE_URL_PARAMS) {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[REDACTED]');
        hasRedacted = true;
      }
    }
    return hasRedacted ? parsed.toString() : url;
  } catch {
    return url;
  }
}

function isUrl(value: string): boolean {
  return (
    value.startsWith('http://') ||
    value.startsWith('https://') ||
    value.startsWith('wss://') ||
    value.startsWith('ws://')
  );
}

function serializeValue(value: unknown, depth: number): unknown {
  if (depth > MAX_SERIALIZATION_DEPTH) {
    return Array.isArray(value) ? '[Array]' : '[Object]';
  }

  if (
    value == null ||
    value instanceof Date ||
    value instanceof RegExp ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (typeof value === 'bigint') {
    return value.toString();
  }

  if (typeof value === 'string') {
    let result = value;
    if (result.length > MAX_STRING_LENGTH) {
      result = `${result.slice(0, MAX_STRING_LENGTH)}…`;
    }
    if (isUrl(result)) {
      result = sanitizeUrl(result);
    }
    return result;
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => serializeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    const input = value as Record<string, unknown>;
    const sanitized: Record<string, unknown> = {};

    for (const [key, nestedValue] of Object.entries(input)) {
      if (shouldRedactKey(key)) {
        sanitized[key] = '[REDACTED]';
        continue;
      }

      sanitized[key] = serializeValue(nestedValue, depth + 1);
    }

    return sanitized;
  }

  return String(value);
}

export function sanitizeForSentry<T>(value: T): T {
  return serializeValue(value, 0) as T;
}

function normalizeUnknownError(error: unknown): {
  error: Error | string;
  message: string;
  name?: string;
  ignore: boolean;
} {
  const extracted = extractErrorInfo(error);
  return {
    error: extracted.error,
    message: extracted.message,
    name: extracted.name,
    ignore: isExpectedError(extracted.name, extracted.message),
  };
}

function withSentryScope<T>(
  context: ErrorContext | undefined,
  level: Sentry.SeverityLevel,
  fn: () => T,
) {
  if (!context) {
    return fn();
  }

  const sanitizedContext = sanitizeForSentry(context);

  return Sentry.withScope((scope) => {
    for (const [key, value] of Object.entries(sanitizedContext)) {
      if (value != null && typeof value === 'object' && !Array.isArray(value)) {
        scope.setContext(key, value as Record<string, unknown>);
      } else {
        scope.setExtra(
          key,
          value as string | number | boolean | null | undefined,
        );
      }
    }

    scope.setLevel(level);
    return fn();
  });
}

function captureErrorInternal(
  error: unknown,
  context?: ErrorContext,
  level: Sentry.SeverityLevel = 'error',
): void {
  const normalized = normalizeUnknownError(error);

  if (normalized.ignore) {
    return;
  }

  trackCount(ERROR_EVENT_COUNT_METRIC, 1, {
    level: normalizeMetricKey(level),
    operation: normalizeMetricKey(context?.operation as string | undefined),
    component: normalizeMetricKey(context?.component as string | undefined),
    error_name: normalizeMetricKey(normalized.name),
  });

  const errorMessage = normalized.message;

  console.error(
    context?.operation
      ? `[${context.operation}] ${errorMessage}`
      : errorMessage,
    normalized.error,
  );

  const sendToSentry = () => {
    if (normalized.error instanceof Error) {
      Sentry.captureException(normalized.error);
    } else {
      Sentry.captureMessage(errorMessage, level);
    }
  };

  if (context) {
    withSentryScope(context, level, sendToSentry);
  } else {
    sendToSentry();
  }
}

export function captureAndReportError(
  error: unknown,
  context?: ErrorContext,
  level?: Sentry.SeverityLevel,
): void;
export function captureAndReportError(
  message: string,
  ...details: unknown[]
): void;
export function captureAndReportError(...args: unknown[]): void {
  if (args.length === 0) {
    return;
  }

  if (typeof args[0] === 'string') {
    const [message, ...rest] = args as [string, ...unknown[]];
    const restArgs = [...rest];

    let level: Sentry.SeverityLevel = 'error';
    const severityIndex = restArgs.findIndex(
      (arg) =>
        typeof arg === 'string' &&
        SEVERITY_LEVELS.has(arg as Sentry.SeverityLevel),
    );
    if (severityIndex !== -1) {
      level = restArgs[severityIndex] as Sentry.SeverityLevel;
      restArgs.splice(severityIndex, 1);
    }

    const errorCandidate = restArgs.find((arg) => arg instanceof Error);
    const contextCandidate = restArgs.find((arg) => isPlainObject(arg)) as
      | ErrorContext
      | undefined;
    const extras = restArgs.filter(
      (arg) => arg !== errorCandidate && arg !== contextCandidate,
    );

    const context: ErrorContext =
      contextCandidate != null
        ? {
            ...contextCandidate,
            operation: contextCandidate.operation ?? message,
          }
        : { operation: message };

    if (extras.length > 0) {
      context.consoleArgs = sanitizeForSentry(extras);
    }

    const error = errorCandidate ?? new Error(message);
    captureErrorInternal(error, context, level);
    return;
  }

  const [error, contextOrLevel, maybeLevel] = args as [
    unknown,
    ErrorContext | Sentry.SeverityLevel | undefined,
    Sentry.SeverityLevel | ErrorContext | undefined,
  ];

  let context: ErrorContext | undefined;
  let level: Sentry.SeverityLevel = 'error';

  if (
    typeof contextOrLevel === 'string' &&
    SEVERITY_LEVELS.has(contextOrLevel as Sentry.SeverityLevel)
  ) {
    level = contextOrLevel as Sentry.SeverityLevel;
  } else if (isPlainObject(contextOrLevel)) {
    context = contextOrLevel as ErrorContext;
  }

  if (
    typeof maybeLevel === 'string' &&
    SEVERITY_LEVELS.has(maybeLevel as Sentry.SeverityLevel)
  ) {
    level = maybeLevel as Sentry.SeverityLevel;
  } else if (isPlainObject(maybeLevel)) {
    context = { ...(context ?? {}), ...(maybeLevel as ErrorContext) };
  }

  captureErrorInternal(error, context, level);
}

export function addBreadcrumb(
  message: string,
  category?: string,
  data?: Record<string, unknown>,
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: 'info',
  });
}

interface TrpcFailureDetails {
  path: string;
  type: string;
  input?: unknown;
  error: { name?: string; message?: string };
  durationMs?: number;
  status?: number;
}

export function captureTrpcFailure(details: TrpcFailureDetails): void {
  const error = new Error(details.error.message || 'Unknown tRPC failure');
  error.name = details.error.name || 'TRPCError';

  const attributes: Record<string, string | number | boolean> = {
    path: normalizeMetricKey(details.path),
    type: normalizeMetricKey(details.type),
    error_name: normalizeMetricKey(error.name),
  };

  if (details.status !== undefined) {
    attributes.status_code = details.status;
  }

  trackCount(TRPC_FAILURE_COUNT_METRIC, 1, attributes);

  if (details.durationMs !== undefined && Number.isFinite(details.durationMs)) {
    trackDuration(TRPC_FAILURE_DURATION_METRIC, details.durationMs, attributes);
  }

  captureAndReportError(error, {
    operation: `trpc.${details.type}`,
    path: details.path,
    durationMs: details.durationMs,
    status: details.status,
    input: sanitizeForSentry(details.input),
  });
}
